import React, { useState, useEffect, useMemo, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import GlassCard from '../components/ui/GlassCard';
import Button from '../components/ui/Button';
import MonthNavigation from '../components/calendar/MonthNavigation';
import CalendarFilters from '../components/calendar/CalendarFilters';
import MonthView from '../components/calendar/MonthView';
import DayDetailModal from '../components/calendar/DayDetailModal';
import EventDetailModal from '../components/calendar/EventDetailModal';
import UnscheduledPanel from '../components/calendar/UnscheduledPanel';
import QuickScheduleDialog from '../components/calendar/QuickScheduleDialog';
import { BUSY_TINTS } from '../components/calendar/DayCell';
import { isAssignable } from '../utils/job-status';
import NewJobSheet from '../components/jobs/NewJobSheet';
import JobDetailView from '../components/jobs/JobDetailView';
import AssignJobSheet from '../components/jobs/AssignJobSheet';
import DayNoteEditor from '../components/jobs/DayNoteEditor';
import ExtendJobDialog from '../components/jobs/ExtendJobDialog';
import { addDays, isoDateString, isWeekend } from '../utils/date-utils';
import { isHoliday } from '../utils/holiday-calculator';
import { getMilbankRecentDailyWeather, getMilbankArchivedDailyWeather, getMilbankForecastByDate } from '../services/weather-service';
import { useJobs } from '../hooks/useJobs';
import { useDayNotes } from '../hooks/useDayNotes';
import { useToast } from '../hooks/useToast';
import { calendarDayNoteService } from '../services/calendar-day-note-service';
import { subscribeToCalendarEvents, importCalendarEvents, deleteCalendarEvent } from '../services/calendar-event-service';
import { parseIcs } from '../utils/ics-parser';
import { groupEventsByDate } from '../utils/calendar-helpers';
import { getJobCalendarDates } from '../utils/job-dates-resolver';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { SlidersHorizontal, AlertTriangle, Info, ChevronDown, ChevronUp, CalendarCheck, CalendarPlus, Upload, ArrowUp, ArrowDown, Minus, TrendingUp, CloudSnow } from 'lucide-react';

// Count business days (Mon-Fri, non-holiday) in [from, to], inclusive, and how
// many of those were weather-blocked per `weatherByDate` (from weather-service).
// "Available" for the KPI card means business-day AND not weather-blocked —
// a snowed-out Tuesday shouldn't count against the crew the same way an open
// Tuesday nobody scheduled does.
function countBusinessDays(from, to, weatherByDate = {}) {
  let businessDays = 0;
  let blockedDays = 0;
  let cursor = new Date(from);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(to);
  end.setHours(0, 0, 0, 0);
  while (cursor <= end) {
    if (!isWeekend(cursor) && !isHoliday(cursor)) {
      businessDays++;
      const key = isoDateString(cursor);
      if (weatherByDate[key]?.blocked) blockedDays++;
    }
    cursor = addDays(cursor, 1);
  }
  return { businessDays, blockedDays, available: businessDays - blockedDays };
}

export default function CalendarScreen() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedCrews, setSelectedCrews] = useState([]);
  const [showNewJobSheet, setShowNewJobSheet] = useState(false);
  const [showAssignJobSheet, setShowAssignJobSheet] = useState(false);
  const [showJobDetail, setShowJobDetail] = useState(false);
  const [showDayNoteEditor, setShowDayNoteEditor] = useState(false);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [editingNoteDate, setEditingNoteDate] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [importing, setImporting] = useState(false);
  const icsInputRef = useRef(null);
  const [dragDepth, setDragDepth] = useState(0); // >0 while a file is dragged over the page
  const [forecastByDate, setForecastByDate] = useState({});
  const [detailDate, setDetailDate] = useState(null);   // day pop-up
  const [selectedEvent, setSelectedEvent] = useState(null); // event pop-up
  const [newJobDate, setNewJobDate] = useState(null);   // preset date for Add Job from a day
  const [assignPresetJob, setAssignPresetJob] = useState(null); // job dropped from the Unscheduled panel
  const [assignDate, setAssignDate] = useState(null);           // day it was dropped on
  const [quickSchedule, setQuickSchedule] = useState(null);     // { job, date } — drop → "how many days?"

  // ── Controls panel — starts EXPANDED for quick access ────
  const [controlsCollapsed, setControlsCollapsed] = useState(true);
  // ── Overdue banner — starts collapsed so calendar is front and center ────
  const [overdueBannerCollapsed, setOverdueBannerCollapsed] = useState(true);
  // ── Days-worked KPI card — starts collapsed to a single headline number ──
  const [workedStatsExpanded, setWorkedStatsExpanded] = useState(false);
  const workedStatsRef = useRef(null);
  const [recentWeather, setRecentWeather] = useState({});
  const [lastYearWeather, setLastYearWeather] = useState({});

  const { jobs, loading: jobsLoading, updateJob, deleteJob } = useJobs();
  const { dayNotes, loading: notesLoading } = useDayNotes();
  const toast = useToast();

  // Fetch historical weather once — recent (this month so far) via the
  // forecast endpoint's past_days, and last year's same month via the
  // archive endpoint. Both are free/no-key; failures just leave the KPI
  // card's weather-blocked counts at 0 rather than breaking the page.
  useEffect(() => {
    const now = new Date();
    getMilbankRecentDailyWeather(now.getDate() + 1).then(setRecentWeather).catch(() => {});

    const thisMonth = now.getMonth();
    const lastYear = now.getFullYear() - 1;
    const lyStart = new Date(lastYear, thisMonth, 1);
    const lyEnd   = new Date(lastYear, thisMonth + 1, 0);
    getMilbankArchivedDailyWeather(isoDateString(lyStart), isoDateString(lyEnd)).then(setLastYearWeather).catch(() => {});
  }, []);

  useEffect(() => subscribeToCalendarEvents(setCalendarEvents), []);

  // ~7-day NWS forecast for the weather badges; cached by the service.
  useEffect(() => {
    getMilbankForecastByDate().then(r => setForecastByDate(r?.byDate || {})).catch(() => {});
  }, []);

  // Keyboard: ← / → change month, T jumps to today. Ignored while typing or
  // when a pop-up is open.
  useEffect(() => {
    function handleKey(e) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) return;
      if (document.body.style.overflow === 'hidden') return; // a Modal is open
      if (e.key === 'ArrowLeft')  setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
      else if (e.key === 'ArrowRight') setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
      else if (e.key === 't' || e.key === 'T') setCurrentDate(new Date());
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // Close the floating KPI popover on outside click
  useEffect(() => {
    if (!workedStatsExpanded) return;
    function handleClick(e) {
      if (workedStatsRef.current && !workedStatsRef.current.contains(e.target)) {
        setWorkedStatsExpanded(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [workedStatsExpanded]);

  // Get overdue jobs (scheduled but not completed, past dates)
  const overdueJobs = jobs.filter(job => {
    if (job.status !== 'scheduled') return false;
    if (!job.scheduledDate) return false;
    const scheduled = new Date(job.scheduledDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    scheduled.setHours(0, 0, 0, 0);
    return scheduled < today;
  }).sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate));

  // ── Days Worked This Month vs same month last year ───────────────────────
  // Counts distinct calendar days (across all jobs' workDays entries, excluding
  // cancelled days) worked, then measures that against *weather-adjusted*
  // capacity — business days that were also not snowed/rained/frozen out —
  // so a storm day doesn't get counted as wasted crew capacity the same way
  // an open day nobody scheduled does. Compares this month-to-date against
  // last year's same date range AND last year's full month, both similarly
  // weather-adjusted, for a real apples-to-apples "what to expect" read.
  const workedDaysStats = useMemo(() => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const todayOfMonth = now.getDate();
    const lastYear = thisYear - 1;

    const monthStart         = new Date(thisYear, thisMonth, 1);
    const monthEnd           = new Date(thisYear, thisMonth + 1, 0);
    const lastYearMonthStart = new Date(lastYear, thisMonth, 1);
    const lastYearMonthEnd   = new Date(lastYear, thisMonth + 1, 0);
    const lastYearSameDay    = new Date(lastYear, thisMonth, Math.min(todayOfMonth, lastYearMonthEnd.getDate()));

    const thisMonthDays = new Set();
    const lastYearThroughSameDay = new Set();
    const lastYearTotalDays = new Set();

    jobs.forEach(job => {
      (job.workDays || []).forEach(wd => {
        if (!wd.date || wd.status === 'cancelled') return;
        const d = wd.date instanceof Date ? wd.date : new Date(wd.date);
        if (isNaN(d.getTime())) return;
        const key = d.toISOString().slice(0, 10);

        if (d.getMonth() === thisMonth && d.getFullYear() === thisYear && d.getDate() <= todayOfMonth) {
          thisMonthDays.add(key);
        }
        if (d.getMonth() === thisMonth && d.getFullYear() === lastYear) {
          lastYearTotalDays.add(key);
          if (d.getDate() <= todayOfMonth) lastYearThroughSameDay.add(key);
        }
      });
    });

    const thisMonthCap      = countBusinessDays(monthStart, now, recentWeather);
    const lastYearToDateCap = countBusinessDays(lastYearMonthStart, lastYearSameDay, lastYearWeather);
    const lastYearFullCap   = countBusinessDays(lastYearMonthStart, lastYearMonthEnd, lastYearWeather);
    // Remaining days in the month have no weather data yet (beyond the ~7-day
    // forecast) — treat them as fully available so the projection stays an
    // optimistic "if things go well" estimate rather than assuming storms.
    const remainingCap      = countBusinessDays(addDays(now, 1), monthEnd, {});

    const thisMonthWorked = thisMonthDays.size;
    const utilization     = thisMonthCap.available > 0 ? thisMonthWorked / thisMonthCap.available : 0;

    const lastYearToDateWorked = lastYearThroughSameDay.size;
    const utilizationLastYearToDate = lastYearToDateCap.available > 0 ? lastYearToDateWorked / lastYearToDateCap.available : 0;

    const lastYearFullWorked = lastYearTotalDays.size;
    const utilizationLastYearFull = lastYearFullCap.available > 0 ? lastYearFullWorked / lastYearFullCap.available : 0;

    // Projection: if the rest of the month keeps pace with today's utilization rate.
    const projectedTotal = Math.round(thisMonthWorked + utilization * remainingCap.available);

    const mk = (worked, cap) => ({
      worked,
      available: cap.available,
      businessDays: cap.businessDays,
      blocked: cap.blockedDays,
      idle: Math.max(0, cap.available - worked),
      utilizationPct: cap.available > 0 ? Math.round((worked / cap.available) * 100) : 0,
    });

    return {
      monthLabel: now.toLocaleDateString('en-US', { month: 'long' }),
      lastYear,
      todayOfMonth,
      thisMonth:      mk(thisMonthWorked, thisMonthCap),
      lastYearToDate: mk(lastYearToDateWorked, lastYearToDateCap),
      lastYearFull:   mk(lastYearFullWorked, lastYearFullCap),
      remainingBusinessDays: remainingCap.available,
      projectedTotal,
    };
  }, [jobs, recentWeather, lastYearWeather]);

  const shiftMonth = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
  function handlePrevious() { setCurrentDate(d => shiftMonth(d, -1)); }
  function handleNext()     { setCurrentDate(d => shiftMonth(d, 1)); }

  function handleToday() { setCurrentDate(new Date()); }
  function handleDateChange(newDate) { setCurrentDate(newDate); }

  function handleStatusToggle(status) {
    setSelectedStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  }

  function handleCrewToggle(crew) {
    setSelectedCrews(prev =>
      prev.includes(crew) ? prev.filter(c => c !== crew) : [...prev, crew]
    );
  }

  // Clicking a day opens its pop-up: jobs, events, note, weather, quick actions.
  function handleDayClick(date) {
    setCurrentDate(date);
    setDetailDate(date);
  }

  function handleAddJob()    { setSelectedJob(null); setNewJobDate(null); setShowNewJobSheet(true); }
  function handleAssignJob() { setAssignPresetJob(null); setAssignDate(null); setShowAssignJobSheet(true); }

  // Job dragged from the Unscheduled panel onto a day — ask how many days it
  // takes, then schedule. "More options" falls back to the full Assign sheet.
  function handleJobDrop(date, jobId) {
    const job = jobs.find(j => j.id === jobId);
    if (job) setQuickSchedule({ job, date });
  }

  function handleQuickScheduled(job, workDays) {
    setQuickSchedule(null);
    const n = workDays.length;
    toast.success(`${job.customerName || job.name} scheduled — ${n} day${n === 1 ? '' : 's'} starting ${workDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
  }

  function handleQuickMoreOptions() {
    setAssignPresetJob(quickSchedule.job);
    setAssignDate(quickSchedule.date);
    setQuickSchedule(null);
    setShowAssignJobSheet(true);
  }

  // Day pop-up actions — close it and open the next thing on top of the page
  function handleDayAddJob()    { setSelectedJob(null); setNewJobDate(detailDate); setDetailDate(null); setShowNewJobSheet(true); }
  function handleDayAssignJob() { setAssignPresetJob(null); setAssignDate(detailDate); setDetailDate(null); setShowAssignJobSheet(true); }
  function handleDayEditNote()  { setEditingNoteDate(detailDate); setDetailDate(null); setShowDayNoteEditor(true); }
  function handleDayJobClick(job)     { setDetailDate(null); handleJobClick(job); }
  function handleDayEventClick(event) { setDetailDate(null); setSelectedEvent(event); }

  function handleIcsInput(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be picked again later
    if (file) importIcsFile(file);
  }

  // Drag an .ics file anywhere onto the calendar page to import it
  const isFileDrag = e => Array.from(e.dataTransfer?.types || []).includes('Files');
  function handleDragEnter(e) { if (isFileDrag(e)) { e.preventDefault(); setDragDepth(d => d + 1); } }
  function handleDragLeave(e) { if (isFileDrag(e)) setDragDepth(d => Math.max(0, d - 1)); }
  function handleDragOver(e)  { if (isFileDrag(e)) e.preventDefault(); }
  function handleDrop(e) {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    setDragDepth(0);
    const file = Array.from(e.dataTransfer.files).find(f => /\.ics$/i.test(f.name) || f.type === 'text/calendar');
    if (file) importIcsFile(file);
    else toast.error('Drop an .ics calendar file to import it');
  }

  async function importIcsFile(file) {
    setImporting(true);
    try {
      const events = parseIcs(await file.text());
      if (events.length === 0) {
        toast.error('No events found in that file');
        return;
      }
      await importCalendarEvents(events, file.name);
      toast.success(`Added ${events.length} event${events.length === 1 ? '' : 's'} to the calendar`);
      // Jump to the first imported event so it's visible right away
      const first = events.reduce((a, b) => (b.start < a.start ? b : a));
      setCurrentDate(first.start);
    } catch (error) {
      console.error('❌ Error importing .ics:', error);
      toast.error('Could not import that .ics file');
    } finally {
      setImporting(false);
    }
  }

  async function handleDeleteEvent(event) {
    if (!confirm(`Remove "${event.title}" from the calendar?`)) return;
    try {
      await deleteCalendarEvent(event.id);
      setSelectedEvent(null);
      toast.success('Event removed');
    } catch (error) {
      console.error('❌ Error removing event:', error);
      toast.error('Failed to remove event');
    }
  }

  function handleJobClick(job) { setSelectedJob(job); setShowJobDetail(true); }

  function handleEditJob(job) {
    setSelectedJob(job);
    setShowJobDetail(false);
    setShowNewJobSheet(true);
  }

  async function handleScheduleJob(job) {
    setSelectedJob(job);
    setShowJobDetail(false);
    setShowAssignJobSheet(true);
  }

  async function handleCompleteJob(job) {
    if (!confirm(`Mark "${job.customerName || job.name}" as completed?`)) return;
    try {
      await updateJob(job.id, { status: 'completed' });
      setShowJobDetail(false);
      toast.success('Job marked as complete');
    } catch (error) {
      console.error('❌ Error completing job:', error);
      toast.error('Failed to complete job');
    }
  }

  async function handleCancelJob(job) {
    if (!confirm(`Cancel job for "${job.customerName || job.name}"?`)) return;
    try {
      await updateJob(job.id, { status: 'cancelled' });
      setShowJobDetail(false);
      toast.success('Job cancelled');
    } catch (error) {
      console.error('❌ Error cancelling job:', error);
      toast.error('Failed to cancel job');
    }
  }

  async function handleDeleteJob(job) {
    if (!confirm(`Permanently delete job for "${job.customerName || job.name}"? This CANNOT be undone!`)) return;
    try {
      await deleteJob(job.id);
      setShowJobDetail(false);
      toast.success('Job deleted');
    } catch (error) {
      console.error('❌ Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  }

  async function handleStatusChange(job, newStatus) {
    try {
      await updateJob(job.id, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('❌ Error updating status:', error);
      toast.error('Failed to update status');
    }
  }

  async function handleSaveNote(noteData) {
    if (!editingNoteDate) return;
    try {
      const dateISO = isoDateString(editingNoteDate);
      if (noteData === null) {
        await calendarDayNoteService.deleteDayNote(dateISO);
        toast.success('Note deleted');
      } else {
        await calendarDayNoteService.saveDayNote(dateISO, noteData);
        toast.success('Note saved');
      }
    } catch (error) {
      console.error('❌ Error saving note:', error);
      toast.error('Failed to save note');
    }
  }

  const allScheduledJobs = jobs.filter(job =>
    job.scheduledDate || (job.workDays && job.workDays.length > 0)
  );

  const breadcrumb = 'Dashboard › Calendar › Month View';

  // Everything shown in the day pop-up
  const eventsByDate = useMemo(() => groupEventsByDate(calendarEvents), [calendarEvents]);
  const detailISO    = detailDate ? isoDateString(detailDate) : null;
  const detailJobs   = detailISO
    ? allScheduledJobs.filter(job => getJobCalendarDates(job).some(d => isoDateString(d) === detailISO))
    : [];

  if (jobsLoading || notesLoading) {
    return (
      <AppLayout title="Calendar" breadcrumb={breadcrumb}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
          <LoadingSpinner size="large" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Calendar"
      breadcrumb={breadcrumb}
      topBarActions={
        <>
          <Button size="small" onClick={handleAddJob}>+ Add Job</Button>
          <Button variant="secondary" size="small" onClick={handleAssignJob}>Assign Job</Button>
          <Button variant="secondary" size="small" onClick={() => icsInputRef.current?.click()} disabled={importing}>
            <CalendarPlus size={14} strokeWidth={2} style={{ marginRight: '4px', verticalAlign: '-2px' }} />
            {importing ? 'Importing…' : 'Import .ics'}
          </Button>
          <input
            ref={icsInputRef}
            type="file"
            accept=".ics,text/calendar"
            onChange={handleIcsInput}
            style={{ display: 'none' }}
          />
        </>
      }
    >
      {/* ── Month/year label — always visible, independent of Controls state ── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '10px',
        marginBottom: '10px',
      }}>
        <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', paddingTop: '5px' }}>
          {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </div>

        {/* Days Worked KPI — utilization headline pill; popover floats on top, doesn't push the calendar */}
        <div ref={workedStatsRef} style={{ position: 'relative' }}>
          <div
            onClick={() => setWorkedStatsExpanded(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '8px 12px', minWidth: '270px',
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--brand-brown-tint-line)',
              borderRadius: workedStatsExpanded ? '12px 12px 0 0' : '12px',
              cursor: 'pointer', userSelect: 'none',
            }}
          >
            <CalendarCheck size={14} strokeWidth={2} color="var(--brand-brown)" />
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
              <strong style={{ color: 'var(--text-primary)', fontSize: '13px' }}>{workedDaysStats.thisMonth.utilizationPct}%</strong> utilized this month
            </span>
            {workedDaysStats.thisMonth.utilizationPct === workedDaysStats.lastYearToDate.utilizationPct ? (
              <Minus size={12} strokeWidth={2.5} color="var(--text-tertiary)" />
            ) : workedDaysStats.thisMonth.utilizationPct > workedDaysStats.lastYearToDate.utilizationPct ? (
              <ArrowUp size={12} strokeWidth={2.5} color="var(--status-text-bold-green)" />
            ) : (
              <ArrowDown size={12} strokeWidth={2.5} color="var(--status-text-bold-red)" />
            )}
            <span style={{ flex: 1 }} />
            {workedStatsExpanded
              ? <ChevronUp size={13} strokeWidth={2} color="var(--text-tertiary)" />
              : <ChevronDown size={13} strokeWidth={2} color="var(--text-tertiary)" />}
          </div>

          {workedStatsExpanded && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, zIndex: 300,
              width: '420px', maxWidth: '90vw',
              background: 'var(--bg-card)',
              border: '1px solid var(--brand-brown-tint-line)',
              borderTop: 'none',
              borderRadius: '0 0 12px 12px',
              boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
              padding: '14px 16px 16px',
            }}>
              {/* Column headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '84px 1fr 1fr 1fr', gap: '6px', marginBottom: '8px' }}>
                <div />
                <ColHeader label="This month" sub={`thru day ${workedDaysStats.todayOfMonth}`} />
                <ColHeader label={`Last ${workedDaysStats.monthLabel}`} sub={`thru day ${workedDaysStats.todayOfMonth}`} />
                <ColHeader label={`Full ${workedDaysStats.monthLabel} '${String(workedDaysStats.lastYear).slice(-2)}`} sub="whole month" />
              </div>

              <StatRow label="Worked"    a={workedDaysStats.thisMonth.worked}    b={workedDaysStats.lastYearToDate.worked}    c={workedDaysStats.lastYearFull.worked} />
              <StatRow label="Available" a={workedDaysStats.thisMonth.available} b={workedDaysStats.lastYearToDate.available} c={workedDaysStats.lastYearFull.available} />
              <StatRow
                label="Idle / off" emphasize
                a={workedDaysStats.thisMonth.idle} b={workedDaysStats.lastYearToDate.idle} c={workedDaysStats.lastYearFull.idle}
              />
              <StatRow
                label="Weather-blocked" icon={<CloudSnow size={11} strokeWidth={2} color="var(--text-tertiary)" />}
                a={workedDaysStats.thisMonth.blocked} b={workedDaysStats.lastYearToDate.blocked} c={workedDaysStats.lastYearFull.blocked}
              />
              <StatRow
                label="Utilization" isPct
                a={workedDaysStats.thisMonth.utilizationPct} b={workedDaysStats.lastYearToDate.utilizationPct} c={workedDaysStats.lastYearFull.utilizationPct}
              />

              <div style={{
                marginTop: '12px', paddingTop: '10px',
                borderTop: '1px dashed var(--brand-brown-tint-line)',
                display: 'flex', alignItems: 'flex-start', gap: '7px',
              }}>
                <TrendingUp size={13} strokeWidth={2} color="var(--brand-brown)" style={{ flexShrink: 0, marginTop: '1px' }} />
                <span style={{ fontSize: '11.5px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  At this pace, expect <strong style={{ color: 'var(--text-primary)' }}>~{workedDaysStats.projectedTotal} days</strong> worked
                  by month-end <span style={{ color: 'var(--text-tertiary)' }}>({workedDaysStats.remainingBusinessDays} business days left)</span>
                </span>
              </div>
              <div style={{ marginTop: '8px', fontSize: '10px', color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                "Idle" and "Weather-blocked" exclude weekends/holidays automatically. A day counts as weather-blocked
                if it had ≥1&quot; snow, ≥0.5&quot; rain, or a high of 20°F or below in Milbank, SD.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Controls toggle bar ────────────────────────────────────────────── */}
      <div
        onClick={() => setControlsCollapsed(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 16px',
          marginBottom: controlsCollapsed ? '16px' : '0',
          background: 'var(--bg-tertiary)',
          border: '1px solid var(--brand-brown-tint-line)',
          borderRadius: controlsCollapsed ? '12px' : '12px 12px 0 0',
          boxShadow: '0 2px 8px var(--brand-brown-tint-soft)',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <SlidersHorizontal size={14} strokeWidth={2} color="var(--text-primary)" />
          <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
            Calendar Controls
          </span>
          {overdueJobs.length > 0 && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              background: 'var(--pill-warning-bg)', color: 'var(--pill-warning-text)',
              borderRadius: '999px', fontSize: '11px', fontWeight: '800',
              padding: '1px 8px',
            }}>
              <AlertTriangle size={12} strokeWidth={2} /> {overdueJobs.length} overdue
            </span>
          )}
          {(selectedStatuses.length > 0 || selectedCrews.length > 0) && (
            <span style={{
              background: 'var(--brand-brown-tint-med)', color: 'var(--brand-brown)',
              borderRadius: '999px', fontSize: '11px', fontWeight: '700',
              padding: '1px 8px',
            }}>
              Filters active
            </span>
          )}
        </div>
        <span style={{
          display: 'flex', alignItems: 'center', gap: '4px',
          fontSize: '11px', fontWeight: '700', color: 'var(--brand-brown)',
          padding: '3px 10px',
          background: 'var(--brand-brown-tint-med)',
          borderRadius: '6px',
        }}>
          {controlsCollapsed ? <ChevronDown size={12} strokeWidth={2.5} /> : <ChevronUp size={12} strokeWidth={2.5} />}
          {controlsCollapsed ? 'Show Controls' : 'Hide Controls'}
        </span>
      </div>

      {/* ── Collapsible panel ──────────────────────────────────────────────── */}
      {!controlsCollapsed && (
        <div style={{
          background: 'var(--bg-tertiary)',
          border: '1px solid var(--brand-brown-tint-line)',
          borderTop: 'none',
          borderRadius: '0 0 12px 12px',
          padding: '16px 20px 20px',
          marginBottom: '16px',
          boxShadow: '0 4px 12px var(--brand-brown-tint-soft)',
        }}>
          {/* Overdue Banner */}
          {overdueJobs.length > 0 && (
            <div
              onClick={e => { e.stopPropagation(); setOverdueBannerCollapsed(v => !v); }}
              style={{
                background: 'var(--overdue-bg-gradient)',
                border: '2px solid var(--overdue-border)',
                borderRadius: '10px',
                padding: overdueBannerCollapsed ? '8px 16px' : '14px 16px',
                marginBottom: '16px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                boxShadow: '0 2px 8px var(--pill-warning-tint)',
                cursor: 'pointer', userSelect: 'none',
                transition: 'padding 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AlertTriangle size={18} strokeWidth={2} color="var(--overdue-text)" />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--overdue-text)' }}>
                    {overdueJobs.length} Overdue Job{overdueJobs.length !== 1 ? 's' : ''} Need Attention
                    {overdueBannerCollapsed && (
                      <span style={{ fontSize: '12px', fontWeight: '500', color: 'var(--overdue-text-soft)', marginLeft: '8px' }}>
                        — {overdueJobs.map(j => j.customerName || j.name).join(', ')}
                      </span>
                    )}
                  </div>
                  {!overdueBannerCollapsed && (
                    <div style={{ fontSize: '12px', color: 'var(--overdue-text-soft)', marginTop: '2px' }}>
                      {overdueJobs.map(j => j.customerName || j.name).join(', ')}
                    </div>
                  )}
                </div>
              </div>
              <span style={{ display: 'flex', fontWeight: '700', color: 'var(--overdue-text)', padding: '3px 8px', background: 'var(--pill-warning-tint)', borderRadius: '5px', flexShrink: 0 }}>
                {overdueBannerCollapsed ? <ChevronDown size={13} strokeWidth={2.5} /> : <ChevronUp size={13} strokeWidth={2.5} />}
              </span>
            </div>
          )}

          {/* Navigation */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <MonthNavigation
              currentDate={currentDate}
              onPrevious={handlePrevious}
              onNext={handleNext}
              onToday={handleToday}
              onDateChange={handleDateChange}
            />
          </div>

          {/* Info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 12px', background: 'var(--bg-card-tint)', borderRadius: '8px', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
            <Info size={13} strokeWidth={2} style={{ flexShrink: 0 }} /> All jobs with dates are visible. Use filters below for visual reference.
          </div>

          {/* Filters */}
          <CalendarFilters
            selectedStatuses={selectedStatuses}
            onStatusToggle={handleStatusToggle}
            selectedCrews={selectedCrews}
            onCrewToggle={handleCrewToggle}
          />
        </div>
      )}

      {/* ── Calendar + Unscheduled panel ─────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
      {/* Calendar (also an .ics drop zone) */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{ position: 'relative', flex: 1, minWidth: 0 }}
      >
        <GlassCard style={{ minHeight: '600px' }}>
          <MonthView
            currentDate={currentDate}
            jobs={allScheduledJobs}
            events={calendarEvents}
            forecastByDate={forecastByDate}
            dayNotes={dayNotes}
            onDayClick={handleDayClick}
            onJobClick={handleJobClick}
            onEventClick={setSelectedEvent}
            onJobDrop={handleJobDrop}
          />
        </GlassCard>

        {dragDepth > 0 && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 50,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px',
            background: 'rgba(253, 251, 248, 0.7)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            border: '2px dashed var(--brand-brown)', borderRadius: '16px',
            color: 'var(--brand-brown)', fontSize: '16px', fontWeight: '700',
            pointerEvents: 'none',
          }}>
            <Upload size={28} strokeWidth={2} />
            Drop the .ics file to add its events
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '8px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
          {/* Legend for the busy-day shading */}
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            Fewer jobs
            {BUSY_TINTS.map((tint, i) => (
              <span key={i} style={{ width: '14px', height: '14px', borderRadius: '3px', background: tint || 'var(--bg-card)', border: '1px solid var(--brand-brown-tint-line)' }} />
            ))}
            More jobs
          </span>
          <span>Tip: click a day for details · drag an .ics file here to import · ← → change month · T for today</span>
        </div>
      </div>

      <UnscheduledPanel jobs={jobs.filter(isAssignable)} onJobClick={handleJobClick} />
      </div>

      <DayDetailModal
        isOpen={!!detailDate}
        onClose={() => setDetailDate(null)}
        date={detailDate}
        jobs={detailJobs}
        events={detailISO ? eventsByDate[detailISO] || [] : []}
        note={detailISO ? dayNotes[detailISO] : null}
        forecast={detailISO ? forecastByDate[detailISO] : null}
        onJobClick={handleDayJobClick}
        onEventClick={handleDayEventClick}
        onEditNote={handleDayEditNote}
        onAddJob={handleDayAddJob}
        onAssignJob={handleDayAssignJob}
      />

      <QuickScheduleDialog
        isOpen={!!quickSchedule}
        onClose={() => setQuickSchedule(null)}
        job={quickSchedule?.job}
        date={quickSchedule?.date}
        forecastByDate={forecastByDate}
        updateJob={updateJob}
        onScheduled={handleQuickScheduled}
        onMoreOptions={handleQuickMoreOptions}
      />

      <EventDetailModal
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onDelete={handleDeleteEvent}
      />

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {selectedJob && (
        <ExtendJobDialog
          isOpen={showExtendDialog}
          onClose={() => setShowExtendDialog(false)}
          job={selectedJob}
        />
      )}

      <NewJobSheet
        isOpen={showNewJobSheet}
        onClose={() => setShowNewJobSheet(false)}
        presetDate={newJobDate}
        editingJob={selectedJob}
      />

      <AssignJobSheet
        isOpen={showAssignJobSheet}
        onClose={() => setShowAssignJobSheet(false)}
        presetDate={assignDate || currentDate}
        presetJob={assignPresetJob}
      />

      <DayNoteEditor
        isOpen={showDayNoteEditor}
        onClose={() => setShowDayNoteEditor(false)}
        onSave={handleSaveNote}
        date={editingNoteDate}
        initialNote={editingNoteDate ? dayNotes[isoDateString(editingNoteDate)] : null}
      />

      {selectedJob && (
        <JobDetailView
          job={selectedJob}
          isOpen={showJobDetail}
          onClose={() => setShowJobDetail(false)}
          onEdit={handleEditJob}
          onSchedule={handleScheduleJob}
          onComplete={handleCompleteJob}
          onCancel={handleCancelJob}
          onDelete={handleDeleteJob}
          onStatusChange={handleStatusChange}
        />
      )}
    </AppLayout>
  );
}

function ColHeader({ label, sub }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ fontSize: '9px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{sub}</div>
    </div>
  );
}

function StatRow({ label, a, b, c, icon, isPct, emphasize }) {
  const fmt = v => isPct ? `${v}%` : v;
  const cellColor = v => emphasize && v > 0 ? 'var(--status-text-bold-red)' : 'var(--text-primary)';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '84px 1fr 1fr 1fr', gap: '6px', alignItems: 'center', padding: '5px 0', borderTop: '1px solid var(--brand-brown-tint-line)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10.5px', fontWeight: '600', color: 'var(--text-secondary)' }}>
        {icon}{label}
      </div>
      <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: '700', color: cellColor(a) }}>{fmt(a)}</div>
      <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: '700', color: cellColor(b) }}>{fmt(b)}</div>
      <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: '700', color: cellColor(c) }}>{fmt(c)}</div>
    </div>
  );
}